import styled from 'styled-components';
import { forwardRef, useState } from 'react';
import { PROJECT_THEME } from '../../theme';
import {
    DEFAULT_FAQ_VARIANT,
    FAQIndicator,
    FAQStyleProps,
    FAQVariant,
    getFAQVariant,
} from './variants';

type StyledProps = FAQStyleProps & { $v: FAQVariant };

const Wrapper = styled.div<StyledProps>`
    display: flex;
    flex-direction: column;
    width: 100%;
    box-sizing: border-box;
    ${({ $v }) => $v.section}
`;

const Item = styled.div<StyledProps>`
    box-sizing: border-box;
    ${({ $v }) => $v.item}
`;

const Row = styled.div<StyledProps>`
    display: flex;
    align-items: center;
    gap: 1.2em;
    cursor: pointer;
    user-select: none;
    font-family: ${({ theme }) => theme.font.FONT_SANS_RED_HAT_DISPLAY};
    font-weight: 600;
    font-size: 1.05em;
    line-height: 1.4;
    ${({ $v }) => $v.row}
`;

const Question = styled.div<StyledProps>`
    flex: 1;
    min-width: 0;
    ${({ $v }) => $v.question}
`;

const Index = styled.span<StyledProps>`
    flex: none;
    ${({ $v }) => $v.number}
`;

const Meta = styled.span<StyledProps>`
    flex: none;
    white-space: nowrap;
    ${({ $v }) => $v.metaStyle}
`;

const IndicatorSlot = styled.span<StyledProps>`
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => theme.colour.LIGHT_GREY};
    font-family: ${({ theme }) => theme.font.FONT_MONO_JETBRAINS};
    font-size: 1em;
    line-height: 1;
    ${({ $v }) => ($v.indicatorSide === 'left' ? 'order: -1;' : '')}
    ${({ $v }) => $v.indicatorStyle}
`;

const AnswerCollapse = styled.div<StyledProps>`
    display: grid;
    grid-template-rows: ${({ $open }) => ($open ? '1fr' : '0fr')};
    opacity: ${({ $open }) => ($open ? 1 : 0)};
    transition: grid-template-rows 280ms cubic-bezier(0.2, 0, 0, 1), opacity 200ms ease;
    ${({ $v }) => $v.collapse}
`;

const AnswerClip = styled.div`
    overflow: hidden;
    min-height: 0;
`;

const Answer = styled.p<StyledProps>`
    margin: 0;
    padding-top: 0.8em;
    font-family: ${({ theme }) => theme.font.FONT_SANS_RED_HAT_DISPLAY};
    font-weight: 400;
    font-size: 0.95em;
    line-height: 1.65;
    color: ${({ theme }) => theme.colour.OFF_WHITE};
    opacity: 0.75;
    ${({ $v }) => $v.answer}
`;

const Glyph = styled.svg<{ $open: boolean; $kind: FAQIndicator }>`
    width: 1em;
    height: 1em;
    transition: transform 240ms cubic-bezier(0.2, 0, 0, 1);
    transform: ${({ $open, $kind }) => {
        if (!$open) return 'rotate(0deg)';
        if ($kind === 'square') return 'rotate(45deg)';
        if ($kind === 'arrow') return 'rotate(90deg)';
        return 'rotate(180deg)';
    }};
`;

const PlusBar = styled.line<{ $open: boolean }>`
    transform-origin: center;
    transition: transform 240ms cubic-bezier(0.2, 0, 0, 1), opacity 240ms ease;
    transform: ${({ $open }) => ($open ? 'scaleY(0)' : 'scaleY(1)')};
    opacity: ${({ $open }) => ($open ? 0 : 1)};
`;

function Indicator({ kind, open }: { kind: FAQIndicator; open: boolean }) {
    switch (kind) {
        case 'plus':
            return (
                <svg viewBox="0 0 16 16" width="1em" height="1em" aria-hidden>
                    <line x1="2" y1="8" x2="14" y2="8" stroke="currentColor" strokeWidth="1.5" />
                    <PlusBar
                        $open={open}
                        x1="8"
                        y1="2"
                        x2="8"
                        y2="14"
                        stroke="currentColor"
                        strokeWidth="1.5"
                    />
                </svg>
            );
        case 'chevron':
            return (
                <Glyph viewBox="0 0 16 16" $open={open} $kind={kind} aria-hidden>
                    <polyline
                        points="3,6 8,11 13,6"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                    />
                </Glyph>
            );
        case 'caret':
            return (
                <Glyph viewBox="0 0 16 16" $open={open} $kind={kind} aria-hidden>
                    <polygon points="4,5 12,5 8,12" fill="currentColor" />
                </Glyph>
            );
        case 'arrow':
            return (
                <Glyph viewBox="0 0 16 16" $open={open} $kind={kind} aria-hidden>
                    <line x1="2" y1="8" x2="13" y2="8" stroke="currentColor" strokeWidth="1.5" />
                    <polyline
                        points="9,4 13,8 9,12"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                    />
                </Glyph>
            );
        case 'square':
            return (
                <Glyph viewBox="0 0 16 16" $open={open} $kind={kind} aria-hidden>
                    <rect x="4" y="4" width="8" height="8" fill="currentColor" />
                </Glyph>
            );
        case 'bracket':
            return <span aria-hidden>{open ? '[ − ]' : '[ + ]'}</span>;
        case 'slash':
            return <span aria-hidden>{open ? '//' : '/*'}</span>;
        default:
            return null;
    }
}

type FAQProps = {
    question: string;
    answer: string;
    meta?: string;
    variant: FAQVariant;
    index: number;
};

function FAQ({ question, answer, meta, variant, index }: FAQProps) {
    const [open, setOpen] = useState(false);

    const styleProps: StyledProps = {
        $v: variant,
        $open: open,
        $index: index,
        $accent: PROJECT_THEME.colour.PALETTE[index % PROJECT_THEME.colour.PALETTE.length],
    };

    const metaText =
        variant.meta === 'label'
            ? meta
            : variant.meta === 'index'
            ? String(index + 1).padStart(2, '0')
            : variant.meta === 'status'
            ? open
                ? 'collapse'
                : 'expand'
            : undefined;

    return (
        <Item {...styleProps}>
            <Row
                {...styleProps}
                role="button"
                tabIndex={0}
                aria-expanded={open}
                onClick={() => setOpen(!open)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setOpen(!open);
                    }
                }}
            >
                {variant.numbered && (
                    <Index {...styleProps}>{String(index + 1).padStart(2, '0')}</Index>
                )}
                <Question {...styleProps}>{question}</Question>
                {metaText && (
                    <Meta {...styleProps} aria-hidden>
                        {metaText}
                    </Meta>
                )}
                {variant.indicator !== 'none' && (
                    <IndicatorSlot {...styleProps}>
                        <Indicator kind={variant.indicator} open={open} />
                    </IndicatorSlot>
                )}
            </Row>
            <AnswerCollapse {...styleProps} aria-hidden={!open}>
                <AnswerClip>
                    <Answer {...styleProps}>{answer}</Answer>
                </AnswerClip>
            </AnswerCollapse>
        </Item>
    );
}

export type FAQEntry = string | { answer: string; meta?: string };

type FAQSectionProps = {
    children?: Record<string, FAQEntry>;
    variant?: number;
};

const FAQSection = forwardRef<HTMLDivElement, FAQSectionProps>(
    ({ children, variant = DEFAULT_FAQ_VARIANT }, ref) => {
        const config = getFAQVariant(variant);

        const sectionProps: StyledProps = {
            $v: config,
            $open: false,
            $index: 0,
            $accent: PROJECT_THEME.colour.PALETTE[0],
        };

        return (
            <Wrapper ref={ref} data-physics {...sectionProps}>
                {Object.entries(children ?? {}).map(([question, entry], index) => (
                    <FAQ
                        key={question}
                        question={question}
                        answer={typeof entry === 'string' ? entry : entry.answer}
                        meta={typeof entry === 'string' ? undefined : entry.meta}
                        variant={config}
                        index={index}
                    />
                ))}
            </Wrapper>
        );
    }
);

FAQSection.displayName = 'FAQSection';

export default FAQSection;